/*
 * Russian-locale formatting.
 *
 * Written out rather than delegated to `Intl` in every call site because two of
 * these need rules `Intl` does not give: byte units abbreviated the way the rest
 * of the product abbreviates them, and the three-form plural Russian actually
 * uses (1 страница / 2 страницы / 5 страниц).
 */

const NBSP = ' '

/** Space-grouped thousands, e.g. `1 214`. */
export function num(value: number): string {
  return Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
}

const BYTE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] as const

/** `412 МБ`, `18,4 МБ` — one decimal below 100, none above, comma separator. */
export function bytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return `0${NBSP}Б`
  let size = value
  let unit = 0
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024
    unit += 1
  }
  const text =
    unit === 0 || size >= 100 ? String(Math.round(size)) : size.toFixed(1).replace('.', ',')
  return `${text}${NBSP}${BYTE_UNITS[unit]}`
}

/** Signed byte delta, for a commit's effect on the plane. */
export function byteDelta(value: number): string {
  if (value === 0) return `±0${NBSP}Б`
  return `${value > 0 ? '+' : '−'}${bytes(Math.abs(value))}`
}

/**
 * Three-form Russian plural. `forms` is `[one, few, many]`:
 * `plural(21, ['страница', 'страницы', 'страниц'])` → `страница`.
 */
export function plural(value: number, forms: [string, string, string]): string {
  const n = Math.abs(Math.round(value)) % 100
  const last = n % 10
  if (n > 10 && n < 20) return forms[2]
  if (last > 1 && last < 5) return forms[1]
  if (last === 1) return forms[0]
  return forms[2]
}

export function counted(value: number, forms: [string, string, string]): string {
  return `${num(value)} ${plural(value, forms)}`
}

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  // A naive timestamp from the API is UTC; without the marker a browser reads it
  // as local time and every age is wrong by the viewer's offset.
  const normalised = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
  const date = new Date(normalised)
  return Number.isNaN(date.getTime()) ? null : date
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** `12 минут назад`, `2 дня назад`, `03.07.2026`. */
export function ago(value: string | null | undefined, now = Date.now()): string {
  const date = parse(value)
  if (!date) return '—'
  const delta = now - date.getTime()
  if (delta < 0) return 'только что'
  if (delta < MINUTE) return 'только что'
  if (delta < HOUR) {
    const minutes = Math.floor(delta / MINUTE)
    return `${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])} назад`
  }
  if (delta < DAY) {
    const hours = Math.floor(delta / HOUR)
    return `${hours} ${plural(hours, ['час', 'часа', 'часов'])} назад`
  }
  if (delta < 7 * DAY) {
    const days = Math.floor(delta / DAY)
    return `${days} ${plural(days, ['день', 'дня', 'дней'])} назад`
  }
  return date.toLocaleDateString('ru-RU')
}

/** `14:03` for today, `25.07` for anything older — the git-log column. */
export function shortStamp(value: string | null | undefined, now = Date.now()): string {
  const date = parse(value)
  if (!date) return '—'
  const today = new Date(now)
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  if (sameDay) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

export function dateTime(value: string | null | undefined): string {
  const date = parse(value)
  if (!date) return '—'
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Elapsed wall time between two API timestamps, e.g. `4,2 с`. */
export function duration(from: string | null | undefined, to: string | null | undefined): string {
  const start = parse(from)
  const end = parse(to)
  if (!start || !end) return '—'
  const seconds = (end.getTime() - start.getTime()) / 1000
  if (seconds < 0) return '—'
  if (seconds < 60) return `${seconds.toFixed(1).replace('.', ',')}${NBSP}с`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}${NBSP}мин${NBSP}${Math.round(seconds % 60)}${NBSP}с`
}

export function shortSha(sha: string | null | undefined, length = 7): string {
  if (!sha) return '—'
  return sha.slice(0, length)
}

/** Percentage of a limit, clamped so an over-quota base still renders a bar. */
export function percent(used: number, limit: number | null | undefined): number | null {
  if (limit === null || limit === undefined) return null
  if (limit <= 0) return used > 0 ? 100 : 0
  return Math.max(0, Math.min(100, (used / limit) * 100))
}

/** Last path segment, for a page title fallback. */
export function baseName(path: string): string {
  const segments = path.split('/')
  return segments[segments.length - 1] ?? path
}
